import { X } from 'lucide-react';
import { POIGNEES } from '../usePlacementDeZones';

/**
 * UNE ZONE POSÉE SUR LA PAGE : sa LIGNE D'ÉCRITURE, sa valeur d'exemple, ses poignées.
 *
 * Tout est en POURCENTAGES : `left: 12.34%`, `width: 30%`. C'est ce qui fait que la zone
 * suit l'image quelle que soit la largeur du conteneur, sans un seul calcul de pixel — et
 * c'est le pendant visuel de la règle qui tient tout l'atelier : le front ne manipule que
 * des fractions.
 *
 * ⚠️ LA LIGNE DU BAS N'EST PAS UNE BORDURE, C'EST LA LIGNE D'ÉCRITURE.
 *
 * Boubacar, le 07/09 : « il n'est pas fluide de poser les variables sur le PDF ». La
 * cause n'était pas le geste, elle était l'aveuglement : la zone était un rectangle vide.
 * Or ce rectangle ne dit pas où le texte tombera — c'est sa BASE qui le dit, comme
 * l'énonce `usePlacementDeZones.aimanterALaLigne` : « poser un champ sur la ligne du nom
 * veut dire que sa BASE tombe sur cette ligne, c'est là que le texte s'écrit ». Cette
 * base n'était marquée nulle part, et l'opérateur ne pouvait la découvrir qu'en
 * enregistrant, en régénérant un mandat, et en le lisant.
 *
 * On trace donc la ligne, et on pose la valeur d'exemple dessus, à la taille demandée.
 *
 * ⚠️ ET L'EXEMPLE DÉBORDE AU LIEU D'ÊTRE COUPÉ, VOLONTAIREMENT.
 *
 * Le couper laisserait croire que le mandat le coupera. Il ne le coupe pas : `ecrire_dans`
 * RÉTRÉCIT la valeur jusqu'à ce qu'elle tienne, et ne signale un débordement que si même
 * six points ne suffisent pas. Ce que l'écran montre est donc la place que la valeur
 * demande à la taille choisie — une valeur qui sort de son cadre sortira plus petite,
 * pas tronquée.
 *
 * ⚠️ `touch-action: none` N'EST PAS DÉCORATIF. Sans lui, sur un écran tactile, le
 * navigateur interprète le glisser comme un défilement de page et vole l'événement au
 * bout de quelques pixels : la zone saute puis se fige.
 */
export default function ZoneDeChamp({
  zone, libelle, exemple, ptEnPx, choisie, mesurer, onCommencer, onSupprimer, onChoisir,
}) {
  const style = {
    left: `${zone.x * 100}%`,
    top: `${zone.y * 100}%`,
    width: `${zone.largeur * 100}%`,
    height: `${zone.hauteur * 100}%`,
    touchAction: 'none',
  };

  // La taille de police à l'écran, en pixels, pour la taille en POINTS de la zone.
  // `ptEnPx` vient de la mesure de l'image par `PageDeGabarit` : c'est le seul endroit
  // où l'atelier convertit, et il convertit pour AFFICHER, jamais pour enregistrer.
  const taillePx = ptEnPx ? Math.max(4, (zone.taille || 9) * ptEnPx) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Zone ${libelle}`}
      onPointerDown={(e) => onCommencer(e, zone.id, null, mesurer)}
      onFocus={() => onChoisir(zone.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onChoisir(zone.id); }}
      className={`absolute group cursor-move border rounded-[3px] ${
        choisie
          ? 'border-marque bg-marque/10 z-20'
          : 'border-marque/40 bg-marque/[0.04] hover:bg-marque/10 z-10'}`}
      style={style}
      data-testid={`zone-${zone.champ}`}
      data-zone-id={zone.id}
    >
      <span className="absolute -top-[15px] left-0 text-[9px] leading-none px-1 py-0.5
                       rounded-t bg-marque text-white whitespace-nowrap pointer-events-none">
        {libelle}
      </span>

      {/* LA LIGNE D'ÉCRITURE. Le bas de la zone, et rien d'autre : c'est là que la valeur
          se pose dans le PDF, et c'est ce que l'aimant colle à une ligne du document. */}
      <span
        className="absolute left-0 right-0 bottom-0 h-px bg-marque pointer-events-none"
        data-testid={`zone-ligne-${zone.champ}`}
      />

      {/* LA VALEUR D'EXEMPLE, POSÉE SUR CETTE LIGNE, à la taille demandée. Le serveur
          l'envoie avec le catalogue (`gabarit_zones.EXEMPLES`) : une seconde liste tenue
          ici divergerait au premier champ ajouté. */}
      {exemple && taillePx && (
        <span
          className="absolute bottom-0 whitespace-nowrap pointer-events-none
                     text-neutral-800/80"
          style={{
            fontSize: `${taillePx}px`,
            lineHeight: 1,
            fontFamily: 'Helvetica, Arial, sans-serif',
            left: zone.alignement === 'gauche' ? 0 : undefined,
            right: zone.alignement === 'droite' ? 0 : undefined,
            ...(zone.alignement === 'centre'
              ? { left: '50%', transform: 'translateX(-50%)' } : {}),
          }}
          data-testid={`zone-exemple-${zone.champ}`}
        >
          {exemple}
        </span>
      )}

      {/* La croix n'apparaît qu'au survol ou sur la zone choisie : huit poignées et une
          croix visibles en permanence sur douze zones donneraient un plan illisible. */}
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onSupprimer(zone.id); }}
        aria-label={`Supprimer la zone ${libelle}`}
        className={`absolute -top-[15px] -right-[7px] w-[14px] h-[14px] rounded-full
                    bg-red-500 text-white flex items-center justify-center
                    before:absolute before:-inset-[5px] before:content-['']
                    ${choisie ? '' : 'opacity-0 group-hover:opacity-100'}`}
        data-testid={`zone-supprimer-${zone.champ}`}
      >
        <X className="w-2.5 h-2.5" />
      </button>

      {/* ⚠️ LA POIGNÉE FAIT 7 PX, SA CIBLE EN FAIT 23. Un carré de 7 px ne s'attrape pas
          au doigt : la norme d'accessibilité demande 24 px de côté (WCAG 2.5.8), et un
          doigt en couvre 45. On garde donc le VISUEL à 7 px — huit carrés de 24 px sur
          une zone donneraient un plan illisible — et on élargit la zone de TOUCHE par un
          pseudo-élément invisible. C'est la même astuce que les cases à cocher d'un
          formulaire mobile : ce qu'on voit et ce qu'on touche ne sont pas le même carré. */}
      {choisie && POIGNEES.map((poignee) => (
        <span
          key={poignee}
          onPointerDown={(e) => onCommencer(e, zone.id, poignee, mesurer)}
          className={`absolute w-[7px] h-[7px] bg-white border border-marque rounded-sm
                      before:absolute before:-inset-2 before:content-['']
                      ${POSITIONS[poignee]} ${CURSEURS[poignee]}`}
          style={{ touchAction: 'none' }}
          data-testid={`zone-poignee-${zone.champ}-${poignee}`}
        />
      ))}
    </div>
  );
}

const POSITIONS = {
  nw: '-top-[4px] -left-[4px]', n: '-top-[4px] left-1/2 -translate-x-1/2',
  ne: '-top-[4px] -right-[4px]', e: 'top-1/2 -right-[4px] -translate-y-1/2',
  se: '-bottom-[4px] -right-[4px]', s: '-bottom-[4px] left-1/2 -translate-x-1/2',
  sw: '-bottom-[4px] -left-[4px]', w: 'top-1/2 -left-[4px] -translate-y-1/2',
};

// Le curseur dit ce que la poignée fait AVANT qu'on l'attrape. Sans ça, il faut essayer.
const CURSEURS = {
  nw: 'cursor-nwse-resize', n: 'cursor-ns-resize', ne: 'cursor-nesw-resize',
  e: 'cursor-ew-resize', se: 'cursor-nwse-resize', s: 'cursor-ns-resize',
  sw: 'cursor-nesw-resize', w: 'cursor-ew-resize',
};
